import { AxiosResponse } from 'axios';
import api from '../api';
import { getAssetId } from '../teacher';
import {
  IParentHomeworkDetailResponse,
  IParentHomeworkFiltersResponse,
  IParentHomeworkListQueryParams,
  IParentHomeworkListResponse,
  IParentHomeworkSubmitResponse,
  ParentSubmissionFilesByTask,
} from '../../interface/parentHomework.interface';

// Parent (guardian) homework endpoints. The base URL already includes `/v2`;
// all calls are JWT-authed and scoped server-side to the guardian's children.

const fetchParentHomeworkList = async (
  studentId: string,
  params?: IParentHomeworkListQueryParams,
): Promise<AxiosResponse<IParentHomeworkListResponse>> => {
  const response = await api.get<IParentHomeworkListResponse>(
    `/parent/homework/student/${studentId}`,
    { params },
  );
  return response;
};

const fetchParentHomeworkFilters = async (
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkFiltersResponse>> => {
  const response = await api.get<IParentHomeworkFiltersResponse>(
    `/parent/homework/filters/student/${studentId}`,
  );
  return response;
};

const fetchParentHomeworkDetail = async (
  homeworkId: string,
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkDetailResponse>> => {
  const response = await api.get<IParentHomeworkDetailResponse>(
    `/parent/homework/${homeworkId}/student/${studentId}`,
  );
  return response;
};

/**
 * Submit the child's work using the SAME upload method as homework creation
 * (the backend's documented contract): every file is uploaded first via
 * POST /v2/file (one request per file — no file-count limit, max 100MB
 * combined per task; pdf/word/excel/powerpoint/jpg/png/mp3), then ONE JSON
 * submit references the
 * returned upload_ids per task. The server re-validates size/type against the
 * stored Upload rows and links + stamps everything in a single transaction.
 */
const submitParentHomework = async (
  homeworkId: string,
  studentId: string,
  filesByTask: ParentSubmissionFilesByTask,
): Promise<AxiosResponse<IParentHomeworkSubmitResponse>> => {
  const tasks: Array<{
    detailId: string;
    files: Array<{ upload_id: string; size?: number }>;
  }> = [];

  // Sequential per-task/per-file uploads (mirrors useHomeworkAttachments) so
  // failures surface with the offending file's name.
  for (const [detailId, files] of Object.entries(filesByTask)) {
    if (!files.length) continue;
    const uploaded: Array<{ upload_id: string; size?: number }> = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const uploadResponse = await getAssetId(formData);
      const uploadId = uploadResponse.data?.[0]?.mediaFiles?.[0]?.id;
      if (!uploadId) {
        throw new Error(`Failed to upload "${file.name}". Please try again.`);
      }
      uploaded.push({ upload_id: uploadId, size: file.size });
    }
    tasks.push({ detailId, files: uploaded });
  }

  const response = await api.post<IParentHomeworkSubmitResponse>(
    `/parent/homework/${homeworkId}/student/${studentId}/submit`,
    { tasks },
  );
  return response;
};

export const parentHomeworkService = {
  fetchParentHomeworkList,
  fetchParentHomeworkFilters,
  fetchParentHomeworkDetail,
  submitParentHomework,
};
